import { useEffect, useMemo, useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import { useCurrencies } from 'api/useCurrencies';
import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { errorResponse } from 'utils/errorsTextHelper';
import { showToast } from 'utils/showToast';
import { EToasterStatus } from 'utils/showToast';
import * as Yup from 'yup';

import AcSelect from 'components/AcSelect/AcSelect';

const validationSchema = Yup.object().shape({
  disabledCurrencies: Yup.array().of(Yup.string()).required()
});

interface Currency {
  content: string;
  key: string;
  value: string;
}

const LocalizedCurrencies: React.FC<{ disabled?: boolean }> = ({
  disabled
}) => {
  const { disableCurrencies, getSupportedCurrencies, getDisabledCurrencies } =
    useCurrencies();
  const [disabledCurrencies, setDisabledCurrencies] = useState<string[]>([]);
  const [currenciesList, setCurrenciesList] = useState<Currency[]>([]);

  const isLoading = useMemo(
    () => getDisabledCurrencies.isLoading || getSupportedCurrencies.isLoading,
    [getDisabledCurrencies.isLoading, getSupportedCurrencies.isLoading]
  );

  const selectedCurrencies = currenciesList
    .map((currency) => currency.value)
    .filter((currency) => !disabledCurrencies.includes(currency));

  const { handleSubmit } = useFormik({
    initialValues: {
      disabledCurrencies
    },
    validationSchema: validationSchema,
    onSubmit: () => {
      disableCurrencies.mutate(disabledCurrencies, {
        onSuccess: () => {
          showToast({
            message: 'Currencies updated successfully',
            status: EToasterStatus.SUCCESS
          });
        },
        onError: (error) => {
          if (error instanceof AxiosError) {
            showToast({
              message: errorResponse(error),
              status: EToasterStatus.ERROR
            });
          }
        },
        onSettled: () => {
          getDisabledCurrencies.refetch();
        }
      });
    }
  });

  const handleCurrencySelectionChange = (newSelectedCurrencies: string[]) => {
    const newDisabledCurrencies = currenciesList
      .map((currency) => currency.value)
      .filter((currency) => !newSelectedCurrencies.includes(currency));

    setDisabledCurrencies(newDisabledCurrencies);
    handleSubmit();
  };

  useEffect(() => {
    getSupportedCurrencies.refetch();
    getDisabledCurrencies.refetch();
  }, []);

  useEffect(() => {
    if (!getSupportedCurrencies.data) return;
    setCurrenciesList(
      getSupportedCurrencies.data
        .filter((currency: string) => currency !== 'USD')
        .map((currency: string) => ({
          content: currency,
          key: currency,
          value: currency
        }))
    );
  }, [getSupportedCurrencies.data]);

  useEffect(() => {
    if (!getDisabledCurrencies.data) return;
    setDisabledCurrencies(getDisabledCurrencies.data);
  }, [getDisabledCurrencies.data]);

  useEffect(() => {
    getSupportedCurrencies.refetch();
  }, []);

  return (
    <Box className="settings-card mt-4 flex flex-row items-center justify-between p-9">
      <Box maxWidth={'50%'}>
        <h3 className="text-base-cardForeground font-semibold">
          Currencies to Localize
        </h3>
        <Box>
          <Typography
            fontSize="12px"
            color="#717188"
            mt={1}
            mb={0}
          >
            Select the currencies you want to display.
          </Typography>
        </Box>
      </Box>
      <Stack direction="column" spacing={2}>
        <form onSubmit={handleSubmit}>
          <Stack direction="row" spacing={2}>
            {!isLoading && (
              <AcSelect
                disabled={disabled}
                header="Currencies"
                sx={{ width: 200, padding: '0px' }}
                size="small"
                value={selectedCurrencies}
                onChange={(e) =>
                  handleCurrencySelectionChange(e.target.value as string[])
                }
                items={currenciesList}
                isCheckBox={true}
                multipleSelect={true}
                isSelectAllEnabled={true}
                isApplyBtnEnabled={true}
                setSelectedItems={handleCurrencySelectionChange}
                renderStatusTextOverride={(selected: string | string[]) => {
                  if (Array.isArray(selected) && selected.length > 0) {
                    return `${selected.length} ${selected.length === 1 ? 'currency' : 'currencies'} selected`;
                  }
                  return 'No currencies selected';
                }}
              />
            )}
          </Stack>
        </form>
      </Stack>
    </Box>
  );
};

export default LocalizedCurrencies;